import React from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { Dialog } from '../ui/Dialog';
import { Segmented } from '../ui/Segmented';
import { Switch } from '../ui/Switch';
import { Button } from '../ui/Button';
import { usePreferences } from '../../context/PreferencesContext';
import { SAMPLE_RULES } from '../../lib/highlight';

export const THEME_OPTIONS = [
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
];

const Section = ({ title, children }) => (
  <section className="border-b border-line py-4 first:pt-0 last:border-b-0 last:pb-0">
    <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-faint">{title}</h3>
    {children}
  </section>
);

export const PreferencesDialog = ({ open, onClose }) => {
  const prefs = usePreferences();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Preferences"
      description="Saved in this browser."
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={prefs.resetPreferences}>Reset to defaults</Button>
          <Button variant="primary" onClick={onClose}>Done</Button>
        </>
      )}
    >
      <Section title="Theme">
        <Segmented
          label="Theme"
          value={prefs.theme}
          onChange={(v) => prefs.setPreference('theme', v)}
          options={THEME_OPTIONS}
          stretch
        />
      </Section>
      <Section title="Text size">
        <Segmented
          label="Text size"
          value={prefs.textSize}
          onChange={(v) => prefs.setPreference('textSize', v)}
          options={[
            { value: 'sm', label: 'Small' },
            { value: 'md', label: 'Default' },
            { value: 'lg', label: 'Large' },
            { value: 'xl', label: 'Largest' },
          ]}
          stretch
        />
      </Section>
      <Section title="Result density">
        <Segmented
          label="Result density"
          value={prefs.density}
          onChange={(v) => prefs.setPreference('density', v)}
          options={[
            { value: 'comfortable', label: 'Comfortable' },
            { value: 'compact', label: 'Compact' },
          ]}
          stretch
        />
      </Section>
      <Section title="Highlighting">
        <Switch
          label="Highlight search terms"
          description="Marks your search words in results and details."
          checked={prefs.highlightTerms}
          onChange={(v) => prefs.setPreference('highlightTerms', v)}
        />
        <Switch
          label="Colour-code sample types"
          description="Tints tube and sample keywords, e.g. EDTA, SST, citrate."
          checked={prefs.highlightSamples}
          onChange={(v) => prefs.setPreference('highlightSamples', v)}
        />
        {prefs.highlightSamples && (
          <div className="mt-2 flex flex-wrap gap-1.5 text-xs" aria-label="Sample colour key">
            {SAMPLE_RULES.map((rule) => (
              <span key={rule.name} className={`sample-chip ${rule.className}`}>{rule.name}</span>
            ))}
          </div>
        )}
      </Section>
    </Dialog>
  );
};
